import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { ScopeSelector } from "@packages/core-client/scope/ScopeSelector.tsx";
import { useFeatureFlags } from "@squide/firefly";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { useTeamScopeClient } from "../api/queries/scope.ts";
import { useScopeSegments } from "../hooks/useScopeSegments.tsx";
import { useTeamAdHocFilter } from "../hooks/teamAdHocFilter.ts";
import { useTeamScope } from "../hooks/useTeamScope.ts";
import { useTeamSegment } from "../hooks/useTeamSegment.ts";
import { TeamFilterActions } from "./TeamFilterActions.tsx";
import { TeamFilterPills } from "./TeamFilterPills.tsx";
import { TeamFilterPopover, type TeamFilterPopoverHandle } from "./TeamFilterPopover.tsx";

export function TeamTabsHeading() {
    const filterPopoverRef = useRef<TeamFilterPopoverHandle>(null);
    const handleAddFilter = useCallback(() => filterPopoverRef.current?.open(), []);

    const { "navigation-redesign": isNavigationRedesign } = useFeatureFlags();
    const availableScopes = isNavigationRedesign ? (["reporting-line", "direct-reports"] as const) : undefined;
    const [scope, setScope] = useTeamScope();
    const [segmentId, setSegmentId] = useTeamSegment();
    const adHocFilter = useTeamAdHocFilter();
    const scopeClient = useTeamScopeClient();
    const { data: previews, isPending: isPreviewsPending } = useQuery(createQueryOptions(scopeClient.getPreviews.queryOptions(), { throwOnError: false }));
    const segments = useScopeSegments({ segmentId, adHocFilter, onAddFilter: handleAddFilter });

    return (
        <div className="flex flex-col gap-3">
            <ScopeSelector
                scope={scope}
                onScopeChange={setScope}
                availableScopes={availableScopes}
                segmentId={segmentId}
                onSegmentChange={setSegmentId}
                activeSegmentName={segments.activeSegmentName}
                hasAdHocFilter={!!adHocFilter}
                previews={previews}
                isPreviewsPending={isPreviewsPending}
                trailingContent={
                    segments.isEnabled && (
                        <>
                            <TeamFilterPills />
                            <TeamFilterPopover ref={filterPopoverRef} />
                        </>
                    )
                }
                renderSegmentsSection={segments.isEnabled ? segments.render : undefined}
                onOpenChange={segments.isEnabled ? segments.onPopoverOpenChange : undefined}
            />
            {segments.isEnabled && <TeamFilterActions />}
            {segments.isEnabled && segments.persistentChrome}
        </div>
    );
}
